import { React, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Row } from "react-bootstrap";
import { i18n, _t } from "app/i18n/i18n";
import Button from "react-bootstrap/Button";
import { LoadingSpinner } from "lib/Spinner";
import { isIntranetPath } from "lib/routing";
import { FeaturedPubCard } from "./FeaturedPudCard";
import { cardStyles } from "./styles";
import "./featured.css";
import { useFetchFeaturedPublications } from "../../pages/redesign/publications/api/direct/useFetchFeaturedPublications";
import { useCreateFeaturedSearchRequest } from "../../pages/redesign/publications/api/searchRequest/useCreateFeaturedSearchRequest";
import { createImageUrl } from "../../utils/publicationUrls/publicationUrls";
import { getCoverImage } from "./getCoverImage";

const FEATURED_RESEARCH_COLLAPSED_KEY = "grp-featured-research-collapsed";

export function FeaturedTileView() {
  const lang = i18n.getLanguage();
  const searchRequest = useCreateFeaturedSearchRequest();
  const featuredResults = useFetchFeaturedPublications(searchRequest);
  const [publications, isLoading] = featuredResults;
  const coverImageMapData = useSelector((state) => state.misc.coverImageData);
  const defaultOpen = !!publications?.length;
  const isIntranet = isIntranetPath();

  const [open, setOpen] = useState(() => {
    if (!isIntranet) return defaultOpen;
    const savedCollapsed = localStorage.getItem(FEATURED_RESEARCH_COLLAPSED_KEY);
    return savedCollapsed === null ? defaultOpen : savedCollapsed !== "true";
  });

  const toggleOpen = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (isIntranet) {
      localStorage.setItem(FEATURED_RESEARCH_COLLAPSED_KEY, (!nextOpen).toString());
    }
  };

  useEffect(() => {
    if (defaultOpen && isIntranet) {
      const savedCollapsed = localStorage.getItem(FEATURED_RESEARCH_COLLAPSED_KEY);
      if (savedCollapsed !== null) {
        setOpen(savedCollapsed !== "true");
      } else {
        setOpen(true);
      }
    } else if (defaultOpen) {
      setOpen(true);
    }
  }, [defaultOpen, isIntranet]);

  if (!defaultOpen) return null;

  return (
    <>
      <FeaturedTitle toggleOpen={toggleOpen} isOpen={open} />
      {open && (
        <FeaturedCardRow
          isLoading={isLoading}
          coverImageMapData={coverImageMapData}
          publications={publications}
          lang={lang}
        />
      )}
    </>
  );
}

function FeaturedCardRow({ isLoading, coverImageMapData, publications, lang }) {
  return (
    <>
      <LoadingSpinner className={isLoading ? "d-flex mx-auto pt-5 screen-height" : "d-none"} />
      <Row className={`mx-n2 ${isLoading ? "d-none" : ""}`}>
        {publications?.map((publication) => {
          const styles = getPublicationStyles(publication);
          const imageUrl = hasCoverImage(publication)
            ? createImageUrl(publication.id, "coverImage.png")
            : `/research/japi/content/assets/featured/${getCoverImage(coverImageMapData?.data, publication).image}`;
          return (
            <FeaturedPubCard
              key={publication.id}
              publication={publication}
              styles={styles}
              lang={lang}
              imageUrl={imageUrl}
            />
          );
        })}
      </Row>
    </>
  );
}

function hasCoverImage(publication) {
  return publication?.customData?.coverImage;
}

function getPublicationStyles(publication) {
  const style = publication.style ? cardStyles[publication.style] : cardStyles.default;
  return style;
}

function FeaturedTitle({ className = "", toggleOpen, isOpen = true }) {
  function openLink() {
    window.open("/research/m/Topics/FeaturedResearch", "_blank");
  }
  return (
    <section className="d-flex flex-row align-items-center mb-2 overflow-x-auto title-bar">
      <span className="px-2">{_t("FEATURED RESEARCH")}</span>
      <div className="ml-auto flex-shrink-0 btn-group" role="group">
        <Button
          variant="none"
          className="title-icons"
          onClick={() => {
            toggleOpen();
          }}
        >
          <i className={isOpen ? "fa fa-chevron-up" : "fa fa-chevron-down"} aria-hidden="true" />
        </Button>
        <Button
          variant="none"
          className="title-icons"
          onClick={() => {
            openLink();
          }}
        >
          <i className="fa fa-plus-circle" aria-hidden="true" />
        </Button>
      </div>
    </section>
  );
}
